import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Info } from 'lucide-react';

interface ApprovalTabProps {
  config: Record<string, unknown>;
  setConfig: (config: Record<string, unknown>) => void;
}

export const ApprovalTab: React.FC<ApprovalTabProps> = ({
  config,
  setConfig,
}) => {
  const updateConfig = (key: string, value: unknown) => {
    setConfig({ ...config, [key]: value });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
        <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">
          This section is optional and can be configured later.
        </p>
      </div>

      <Card className="border-dashed">
        <CardContent className="pt-4 space-y-4">
          <div className="space-y-3">
            <Label className="text-sm">Approval Required:</Label>
            <RadioGroup
              value={(config.approval_required as boolean) === true ? 'yes' : 'no'}
              onValueChange={(value) => updateConfig('approval_required', value === 'yes')}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="approval_yes" />
                <Label htmlFor="approval_yes" className="font-normal">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="approval_no" />
                <Label htmlFor="approval_no" className="font-normal">No</Label>
              </div>
            </RadioGroup>
          </div>

          {config.approval_required && (
            <>
              <div className="space-y-2">
                <Label className="text-sm">Approval Threshold ($):</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="e.g., 500"
                  value={(config.approval_threshold as number) || ''}
                  onChange={(e) => updateConfig('approval_threshold', parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  Bills above this amount require approval
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Approval Level:</Label>
                <Select
                  value={(config.approval_level as string) || 'manager'}
                  onValueChange={(value) => updateConfig('approval_level', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select approval level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Property Manager</SelectItem>
                    <SelectItem value="billing_manager">Billing Manager</SelectItem>
                    <SelectItem value="senior_manager">Senior Manager</SelectItem>
                    <SelectItem value="director">Director</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="auto_approve_recurring"
                  checked={(config.auto_approve_recurring as boolean) || false}
                  onCheckedChange={(checked) => updateConfig('auto_approve_recurring', !!checked)}
                />
                <Label htmlFor="auto_approve_recurring" className="text-sm font-normal">
                  Auto-approve recurring bills within 10% variance
                </Label>
              </div>
            </>
          )}
        </CardContent>
      </Card>

    </div>
  );
};
